/**
 * Generates a Marriott mobile deep link URL with the provided parameters
 * @param {Object} params - The deep link parameters
 * @param {string} params.fid - Screen identifier (required) - e.g., 'HP', 'SR', 'HI', 'RD', etc.
 * @param {string} params.sr - Special Rate Code (3-letter code like AAA, MMB, MMF, MM4)
 * @param {string} params.ci - Check-in date in MMDDYYYY format
 * @param {string} params.co - Check-out date in MMDDYYYY format
 * @param {string} params.ct - City name (full city name)
 * @param {string} params.st - State code (2-letter state code)
 * @param {string} params.cn - Country code (2-letter country code)
 * @param {number} params.guests - Number of guests
 * @param {number} params.rooms - Number of rooms
 * @param {string} params.m - Property identifier (5-letter MARSHA code)
 * @param {string} params.ac - Airport code (3-letter code like IAD)
 * @param {string} params.mb - Marriott brand (2-letter chain code)
 * @param {string} params.confirmationno - Confirmation number (8-digit reservation number)
 * @param {string} params.firstname - Guest first name
 * @param {string} params.lastname - Guest last name
 * @param {string} params.bid - Member benefit tier (Basic, Silver, Gold, Platinum, Titanium, Ambassador)
 * @param {string} params.oid - Offer identifier
 * @param {string} params.prid - Promotion identifier (4-character code)
 * @param {string} params.altsearch - Category search trigger ("true" or amenity codes)
 * @param {string} params.flexdates - Flexible dates search ("true")
 * @param {string} params.bdc - Force calendar screen ("Y")
 * @param {string} params.bypassinterrupts - Bypass interrupts ("Y" or "N")
 * @returns {string} The generated Marriott mobile deep link URL
 */
export const generateMarriottDeepLink = (params) => {
  const { fid, ...otherParams } = params;

  // Validate required fid parameter
  if (!fid) {
    throw new Error("fid (screen identifier) is a required parameter");
  }

  // Valid screen identifiers from the documentation
  const validScreens = [
    "HP",
    "SR",
    "SAR",
    "FH",
    "HI",
    "RL",
    "RD",
    "MBO",
    "YR",
    "CI",
    "CO",
    "ISMK",
    "MKLM",
    "CHT",
    "SQ",
    "MD",
    "RST",
    "AS",
    "SPA",
    "FIT",
    "JMR",
    "YA",
    "AH",
    "YAS",
    "PF",
    "IHP",
    "FBP",
    "RPF",
    "CPF",
    "EPF",
    "PI",
    "MMC",
    "WLL",
    "AMB",
    "RW",
    "SC",
    "MS",
    "BMM",
    "PR",
    "PRRG",
    "PRDT"
  ];

  // Warn if using non-standard screen identifier (but don't throw error as app will default to home)
  if (!validScreens.includes(fid)) {
    console.warn(
      `Warning: '${fid}' is not a recognized screen identifier. App will default to home page.`
    );
  }

  // Build the URL parameters manually to avoid encoding issues
  const urlParts = [`fid=${encodeURIComponent(fid)}`];

  // Add all other parameters if they exist
  Object.entries(otherParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      urlParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value.toString())}`);
    }
  });

  // Construct the final deep link URL
  const baseUrl = "marriott://mobile";
  const queryString = urlParts.join("&");
  return `${baseUrl}?${queryString}`;
};